/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { UserFilters } from '@/components/users/UserFilters'
import { UserTable } from '@/components/users/UserTable'
import { UserEditDialog } from '@/components/users/UserEditDialog'
import { UserDeleteDialog } from '@/components/users/UserDeleteDialog'
import { getUsers, deleteUser, getRoles } from '@/api/users/users.api'
import type { UserFilters as UserFiltersType, UserSortField } from '@/api/users/users.api'
import type { UserProfile } from '@/types/auth.types'

export default function ManageUsersPage() {
  const queryClient = useQueryClient()

  // State pro filtry a řazení
  const [filters, setFilters] = useState<UserFiltersType>({
    page: 1,
    perPage: 20,
    sortBy: 'email',
    sortOrder: 'ASC',
  })

  // State pro dialogy
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [deletingUser, setDeletingUser] = useState<UserProfile | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  // Načtení uživatelů
  const { data, isLoading, error } = useQuery({
    queryKey: ['users', filters],
    queryFn: () => getUsers(filters),
  })

  // Načtení rolí pro zobrazení názvů v tabulce
  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  })

  // Mutace pro smazání uživatele
  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('Uživatel byl úspěšně smazán')
      setIsDeleteDialogOpen(false)
      setDeletingUser(null)
    },
    onError: () => {
      toast.error('Chyba při mazání uživatele')
    },
  })

  const handleFiltersChange = (newFilters: UserFiltersType) => {
    setFilters({ ...newFilters, page: 1 })
  }

  const handleSort = (field: UserSortField) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder:
        prev.sortBy === field && prev.sortOrder === 'ASC' ? 'DESC' : 'ASC',
    }))
  }

  const handleEdit = (user: UserProfile) => {
    setEditingUser(user)
    setIsEditDialogOpen(true)
  }

  const handleCreate = () => {
    setEditingUser(null)
    setIsEditDialogOpen(true)
  }

  const handleDelete = (id: string) => {
    const user = data?.data.find((u) => u.id === id)
    if (user) {
      setDeletingUser(user)
      setIsDeleteDialogOpen(true)
    }
  }

  const handleDeleteConfirm = () => {
    if (deletingUser) {
      deleteMutation.mutate(deletingUser.id)
    }
  }

  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['users'] })
  }

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }))
  }

  return (
    <div className="container mx-auto py-6 max-w-7xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Správa uživatelů</h1>
            <p className="text-muted-foreground mt-1">
              Spravujte uživatele a jejich oprávnění
            </p>
          </div>
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Vytvořit uživatele
          </Button>
        </div>

        {/* Filtry */}
        <UserFilters filters={filters} onFiltersChange={handleFiltersChange} />

        {/* Tabulka */}
        {isLoading ? (
          <div className="bg-white rounded-lg border p-8 text-center text-muted-foreground">
            Načítám uživatele...
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg border p-8 text-center text-red-600">
            Chyba při načítání uživatelů
          </div>
        ) : (
          <>
            <UserTable
              users={data?.data || []}
              roles={roles}
              onEdit={handleEdit}
              onDelete={handleDelete}
              sortBy={filters.sortBy}
              sortOrder={filters.sortOrder}
              onSort={handleSort}
            />

            {/* Paginace */}
            {data?.meta && data.meta.totalPages > 1 && (
              <div className="flex items-center justify-between bg-white rounded-lg border p-4">
                <div className="text-sm text-muted-foreground">
                  Stránka {data.meta.page} z {data.meta.totalPages} (celkem{' '}
                  {data.meta.total} uživatelů)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(data.meta.page - 1)}
                    disabled={data.meta.page === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Předchozí
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(data.meta.page + 1)}
                    disabled={data.meta.page === data.meta.totalPages}
                  >
                    Další
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Editační dialog */}
      <UserEditDialog
        user={editingUser}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSuccess={handleEditSuccess}
      />

      {/* Delete dialog */}
      <UserDeleteDialog
        user={deletingUser}
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  )
}
